// /src/pages/ReportsAnalyticsPage.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import {
  Assessment,
  Block,
  DeleteOutline,
  Download,
  Router as RouterIcon,
  Schedule,
} from '@mui/icons-material';
import { useNavbar } from '../context/NavbarContext';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { getAllSites, getGatewayStatus, getTagNamesByProjectId, getUserFromDynamoDB } from '../api/apiService';

const HISTORY_KEY = 'oms_report_download_history';
const MAX_HISTORY = 15;

const shakeAnim = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-6px); }
  20%, 40%, 60%, 80% { transform: translateX(6px); }
`;

const PERIOD_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'custom', label: 'Custom range' },
];

const EXPORT_COLUMNS = [
  'projectId',
  'value',
  'timeTagId',
  'serverName',
  'localTime',
  'timestamp',
  'address',
  'tagName',
  'timestamp_readable',
];

const REPORTS_ALLOWED_ROLES = ['Admin', 'Manager', 'Engineer', 'User'];

const formatUtcForExport = (date) =>
  date.toISOString().replace('T', ' ').slice(0, 19);

const getPeriodRange = (period, customStart, customEnd) => {
  const end = new Date();
  const start = new Date();

  if (period === 'today') {
    start.setUTCHours(0, 0, 0, 0);
  } else if (period === '7d') {
    start.setTime(end.getTime() - 7 * 24 * 60 * 60 * 1000);
  } else if (period === '30d') {
    start.setTime(end.getTime() - 30 * 24 * 60 * 60 * 1000);
  } else if (period === 'custom') {
    if (!customStart || !customEnd) return null;
    const s = new Date(customStart);
    const e = new Date(customEnd);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime()) || e <= s) {
      return null;
    }
    return { start: s, end: e };
  }

  return { start, end };
};

const loadHistory = () => {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const saveHistory = (entries) => {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(entries.slice(0, MAX_HISTORY)));
  } catch {
    // ignore quota / private mode
  }
};

const ReportsAnalyticsPage = () => {
  const { isNavbarOpen } = useNavbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const currentUser = useSelector((state) => state.auth.user);
  const userRole = currentUser?.role;
  const roleAllowedForReports = REPORTS_ALLOWED_ROLES.includes(userRole);
  const presetProjectId = location.state?.projectId || '';

  const [sites, setSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(true);
  const [siteId, setSiteId] = useState(presetProjectId);
  const [tagNames, setTagNames] = useState([]);
  const [tagName, setTagName] = useState('');
  const [loadingTags, setLoadingTags] = useState(false);
  const [columns, setColumns] = useState([]);
  const [shakeColumns, setShakeColumns] = useState(false);
  const [period, setPeriod] = useState('7d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(null); // null = hidden, 0-100
  const [downloadLabel, setDownloadLabel] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [history, setHistory] = useState([]);
  const [gateway, setGateway] = useState(null);
  const [loadingGateway, setLoadingGateway] = useState(false);

  // Role may not be in Redux yet right after login/navigation
  const roleReady = Boolean(userRole);
  const isUserRole = userRole === 'User';

  // Fail closed for Users: never show the reports UI until site assignment is confirmed.
  // (Previously `loadingSites` made canAccessReports true and flashed the full page.)
  const canAccessReports =
    roleReady &&
    roleAllowedForReports &&
    (userRole !== 'User' || (!loadingSites && sites.length > 0));

  const isCheckingAccess =
    !roleReady ||
    (roleAllowedForReports && isUserRole && loadingSites);

  useEffect(() => {
    setHistory(loadHistory());
  }, []);

  useEffect(() => {
    let cancelled = false;

    // Wait until role is known before deciding what to fetch
    if (!roleReady) {
      return undefined;
    }

    if (!roleAllowedForReports) {
      setLoadingSites(false);
      setSites([]);
      return undefined;
    }

    const fetchSites = async () => {
      setLoadingSites(true);
      try {
        const allSites = await getAllSites();
        let visibleSites = Array.isArray(allSites) ? allSites : [];

        // Engineers / Users only see assigned sites (same rule as Sites page)
        if ((userRole === 'Engineer' || userRole === 'User') && currentUser?.email) {
          const userData = await getUserFromDynamoDB(currentUser.email);
          const allowedSites = userData?.sites || (userData?.site?.id ? [userData.site] : []);
          visibleSites = visibleSites.filter((site) =>
            allowedSites.some((us) => us.id === site.id)
          );
        }

        if (!cancelled) {
          setSites(visibleSites);
          // Prefill site when opened from Individual Project "Reports & Analytics" CTA
          if (presetProjectId && visibleSites.some((s) => s.id === presetProjectId)) {
            setSiteId(presetProjectId);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError('Failed to load sites. Please refresh and try again.');
          setSites([]);
        }
      } finally {
        if (!cancelled) setLoadingSites(false);
      }
    };

    fetchSites();
    return () => {
      cancelled = true;
    };
  }, [currentUser, userRole, roleReady, roleAllowedForReports, presetProjectId]);

  useEffect(() => {
    let cancelled = false;
    if (!siteId) {
      setGateway(null);
      setTagNames([]);
      setTagName('');
      return undefined;
    }

    const fetchSiteDetails = async () => {
      setLoadingGateway(true);
      setLoadingTags(true);
      setTagName('');
      setTagNames([]);

      try {
        const [status, tags] = await Promise.all([
          getGatewayStatus(siteId),
          getTagNamesByProjectId(siteId),
        ]);
        if (!cancelled) {
          setGateway(status);
          setTagNames(Array.isArray(tags) ? tags.filter(Boolean).sort() : []);
        }
      } catch {
        if (!cancelled) {
          setGateway(null);
          setTagNames([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingGateway(false);
          setLoadingTags(false);
        }
      }
    };
    fetchSiteDetails();
    return () => {
      cancelled = true;
    };
  }, [siteId]);

  const selectedSite = useMemo(
    () => sites.find((s) => s.id === siteId) || null,
    [sites, siteId]
  );

  const periodLabel = useMemo(() => {
    const opt = PERIOD_OPTIONS.find((p) => p.value === period);
    return opt?.label || period;
  }, [period]);

  const gatewayChip = useMemo(() => {
    if (!siteId) return null;
    if (loadingGateway) {
      return <Chip size="small" label="Checking gateway…" />;
    }
    const status = gateway?.status || 'UNKNOWN';
    const color =
      status === 'ONLINE' ? 'success' : status === 'OFFLINE' ? 'error' : 'default';
    return (
      <Chip
        size="small"
        icon={<RouterIcon sx={{ fontSize: 16 }} />}
        label={`Gateway: ${status}`}
        color={color}
        variant={status === 'UNKNOWN' ? 'outlined' : 'filled'}
      />
    );
  }, [siteId, gateway, loadingGateway]);

  const appendHistory = useCallback((entry) => {
    setHistory((prev) => {
      const next = [entry, ...prev].slice(0, MAX_HISTORY);
      saveHistory(next);
      return next;
    });
  }, []);

  const removeHistoryEntry = useCallback((id) => {
    setHistory((prev) => {
      const next = prev.filter((row) => row.id !== id);
      saveHistory(next);
      return next;
    });
  }, []);

  const clearHistory = useCallback(() => {
    setHistory([]);
    saveHistory([]);
  }, []);

  const handleGenerate = async () => {
    setError('');
    setSuccess('');

    if (!siteId) {
      setError('Select a site to generate a report.');
      return;
    }

    if (!tagName) {
      setError('Select a specific tag. Exporting all tags will time out — pick one tag only.');
      return;
    }

    if (!columns.length) {
      setError('Select at least one column for the Excel export.');
      setShakeColumns(false);
      // Restart animation even if already shaking
      requestAnimationFrame(() => {
        setShakeColumns(true);
        window.setTimeout(() => setShakeColumns(false), 600);
      });
      return;
    }

    const range = getPeriodRange(period, customStart, customEnd);
    if (!range) {
      setError(
        period === 'custom'
          ? 'Custom range requires a valid start and end (end must be after start).'
          : 'Could not resolve the selected period.'
      );
      return;
    }

    setIsExporting(true);
    setDownloadProgress(0);
    setDownloadLabel('Preparing export…');
    setSuccess('');

    let progressTimer = null;
    try {
      const params = new URLSearchParams();
      params.append('projectId', siteId);
      params.append('tagName', tagName);
      params.append('columns', columns.join(','));
      params.append('startTime', formatUtcForExport(range.start));
      params.append('endTime', formatUtcForExport(range.end));

      const API_URL =
        process.env.REACT_APP_EXPORT_TELEMETRY_API_URL ||
        'https://k59dy1jf5h.execute-api.ap-south-1.amazonaws.com/dev/export-telemetry';
      const fullUrl = `${API_URL}?${params.toString()}`;

      // Advance slowly while the Lambda builds the CSV (can take a while)
      progressTimer = window.setInterval(() => {
        setDownloadProgress((prev) => {
          const current = typeof prev === 'number' ? prev : 0;
          if (current >= 80) return current;
          return current + 2;
        });
      }, 350);

      const response = await fetch(fullUrl);
      if (progressTimer) {
        window.clearInterval(progressTimer);
        progressTimer = null;
      }

      if (!response.ok) {
        let message = `Export failed (HTTP ${response.status})`;
        try {
          const errBody = await response.text();
          if (errBody) message = errBody.slice(0, 200);
        } catch {
          // ignore
        }
        throw new Error(message);
      }

      setDownloadLabel('Downloading file…');
      const contentLength = Number(response.headers.get('Content-Length'));
      let blob;

      if (response.body && contentLength > 0) {
        const reader = response.body.getReader();
        const chunks = [];
        let received = 0;
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
          received += value.length;
          const pct = Math.min(99, Math.round((received / contentLength) * 100));
          setDownloadProgress(pct);
          setDownloadLabel(`Downloading… ${pct}%`);
        }
        blob = new Blob(chunks, { type: 'text/csv' });
      } else {
        setDownloadProgress(90);
        blob = await response.blob();
      }

      const safeTag = String(tagName).replace(/[^\w.-]+/g, '_');
      const filename = `telemetry_${siteId}_${safeTag}_${Date.now()}.csv`;
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);

      setDownloadProgress(100);
      setDownloadLabel('Download complete');

      const generatedAt = new Date().toISOString();
      appendHistory({
        id: `${siteId}-${tagName}-${generatedAt}`,
        siteId,
        siteName: selectedSite?.name || siteId,
        tagName,
        period: periodLabel,
        startTime: formatUtcForExport(range.start),
        endTime: formatUtcForExport(range.end),
        generatedAt,
        status: 'Downloaded',
      });
      setSuccess(`Download complete for tag “${tagName}”.`);
    } catch (err) {
      setError(err.message || 'Failed to download report.');
      setDownloadProgress(null);
      setDownloadLabel('');
    } finally {
      if (progressTimer) window.clearInterval(progressTimer);
      window.setTimeout(() => {
        setIsExporting(false);
        setDownloadProgress(null);
        setDownloadLabel('');
      }, 900);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        width: '100%',
        maxWidth: '100%',
        background: theme.palette.background.default,
        margin: 0,
        padding: 0,
        overflowX: 'hidden',
        overflowY: 'auto',
        boxSizing: 'border-box',
      }}
    >
      <Container
        disableGutters
        sx={{
          minHeight: '100vh',
          width: '100%',
          maxWidth: '100% !important',
          flexGrow: 1,
          padding: { xs: '12px', sm: '16px', md: '20px', lg: '24px' },
          marginLeft: {
            xs: isNavbarOpen ? '240px' : '70px',
            sm: isNavbarOpen ? '240px' : '70px',
          },
          marginTop: { xs: '65px', sm: '64px' },
          transition: 'margin-left 0.3s, padding 0.3s',
          overflowX: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 1.5, sm: 2 },
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h4"
              fontWeight={600}
              sx={{
                color: theme.palette.text.primary,
                letterSpacing: '-0.5px',
                fontSize: { xs: '1.35rem', sm: '1.5rem', md: '2rem' },
                position: 'relative',
                pb: 1,
                wordBreak: 'break-word',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  width: 60,
                  height: 4,
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: 2,
                },
              }}
            >
              Reports & Analytics
            </Typography>
          </Box>

          {isCheckingAccess ? (
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, md: 6 },
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
                textAlign: 'center',
              }}
            >
              <CircularProgress size={36} sx={{ mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Loading Reports & Analytics...
              </Typography>
            </Paper>
          ) : !canAccessReports ? (
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 3, md: 5 },
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.error.main, 0.35)}`,
                backgroundColor: alpha(theme.palette.error.main, 0.06),
                textAlign: 'center',
              }}
            >
              <Block sx={{ fontSize: { xs: 44, md: 56 }, color: 'error.main', mb: 1.5 }} />
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{
                  color: 'error.main',
                  letterSpacing: '0.02em',
                  fontSize: { xs: '1.25rem', md: '1.5rem' },
                }}
              >
                Access Denied
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'error.dark',
                  mt: 1.5,
                  fontSize: { xs: '0.9rem', md: '1rem' },
                  px: { xs: 0.5, sm: 2 },
                }}
              >
                You do not have permission to view Reports & Analytics.
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  mt: 1,
                  fontSize: { xs: '0.8rem', md: '0.875rem' },
                  px: { xs: 0.5, sm: 2 },
                }}
              >
                {userRole === 'User'
                  ? 'Reports are available only when a site is assigned to your account. Please contact administrator.'
                  : 'Please contact an administrator to grant access to Reports & Analytics.'}
              </Typography>
            </Paper>
          ) : (
            <>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 2, md: 3 },
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor: theme.palette.background.paper,
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: { xs: 1.5, sm: 2 },
                    flexWrap: 'wrap',
                  }}
                >
                  <Assessment color="primary" />
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    sx={{
                      flexGrow: 1,
                      fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' },
                      minWidth: 0,
                    }}
                  >
                    Generate report
                  </Typography>
                  {gatewayChip}
                </Box>

                <Grid container spacing={{ xs: 1.5, sm: 2 }} alignItems="flex-start">
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth size="small" disabled={loadingSites}>
                      <InputLabel id="report-site-label">Site</InputLabel>
                      <Select
                        labelId="report-site-label"
                        label="Site"
                        value={siteId}
                        onChange={(e) => setSiteId(e.target.value)}
                        MenuProps={{
                          PaperProps: { sx: { maxHeight: 320, maxWidth: '90vw' } },
                        }}
                      >
                        {sites.map((site) => (
                          <MenuItem key={site.id} value={site.id} sx={{ whiteSpace: 'normal' }}>
                            {site.name || site.id}
                            {site.id ? ` (${site.id})` : ''}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {loadingSites && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="caption" color="text.secondary">
                          Loading sites…
                        </Typography>
                      </Box>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl
                      fullWidth
                      size="small"
                      disabled={!siteId || loadingTags}
                      required
                    >
                      <InputLabel id="report-tag-label">Tag (required)</InputLabel>
                      <Select
                        labelId="report-tag-label"
                        label="Tag (required)"
                        value={tagName}
                        onChange={(e) => setTagName(e.target.value)}
                        MenuProps={{
                          PaperProps: { sx: { maxHeight: 320, maxWidth: '90vw' } },
                        }}
                      >
                        {tagNames.map((tag) => (
                          <MenuItem key={tag} value={tag} sx={{ whiteSpace: 'normal', wordBreak: 'break-all' }}>
                            {tag}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {loadingTags && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="caption" color="text.secondary">
                          Loading tags…
                        </Typography>
                      </Box>
                    )}
                    {siteId && !loadingTags && tagNames.length === 0 && (
                      <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
                        No tags found for this site (live telemetry empty).
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="report-period-label">Period</InputLabel>
                      <Select
                        labelId="report-period-label"
                        label="Period"
                        value={period}
                        onChange={(e) => setPeriod(e.target.value)}
                      >
                        {PERIOD_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <FormControl
                      fullWidth
                      size="small"
                      required
                      error={shakeColumns || (Boolean(error) && !columns.length)}
                      sx={{
                        animation: shakeColumns ? `${shakeAnim} 0.55s ease-in-out` : 'none',
                        '& .MuiOutlinedInput-notchedOutline': shakeColumns
                          ? { borderColor: 'error.main', borderWidth: 2 }
                          : undefined,
                        minWidth: 0,
                      }}
                    >
                      <InputLabel id="report-columns-label">Columns</InputLabel>
                      <Select
                        labelId="report-columns-label"
                        id="report-columns"
                        multiple
                        value={columns}
                        onChange={(e) => {
                          const value = e.target.value;
                          setColumns(typeof value === 'string' ? value.split(',') : value);
                          setShakeColumns(false);
                          if (error && error.includes('column')) setError('');
                        }}
                        input={<OutlinedInput label="Columns" />}
                        renderValue={(selected) =>
                          selected.length ? selected.join(', ') : ''
                        }
                        displayEmpty
                        MenuProps={{
                          PaperProps: { sx: { maxHeight: 360, maxWidth: '90vw' } },
                        }}
                        sx={{
                          '& .MuiSelect-select': {
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          },
                        }}
                      >
                        {EXPORT_COLUMNS.map((col) => (
                          <MenuItem key={col} value={col}>
                            <Checkbox checked={columns.indexOf(col) > -1} />
                            <ListItemText primary={col} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {period === 'custom' && (
                    <>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          label="Start"
                          type="datetime-local"
                          size="small"
                          fullWidth
                          value={customStart}
                          onChange={(e) => setCustomStart(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ style: { fontSize: isMobile ? 14 : 16 } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          label="End"
                          type="datetime-local"
                          size="small"
                          fullWidth
                          value={customEnd}
                          onChange={(e) => setCustomEnd(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ style: { fontSize: isMobile ? 14 : 16 } }}
                        />
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        flexWrap: 'wrap',
                        justifyContent: { xs: 'stretch', md: 'flex-end' },
                        alignItems: 'center',
                        height: '100%',
                        pt: { md: 0.25 },
                      }}
                    >
                      <Button
                        variant="contained"
                        fullWidth={isMobile}
                        startIcon={
                          isExporting ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            <Download />
                          )
                        }
                        onClick={handleGenerate}
                        disabled={
                          isExporting ||
                          loadingSites ||
                          loadingTags ||
                          !sites.length ||
                          !siteId ||
                          !tagName
                        }
                        sx={{
                          minWidth: { xs: '100%', sm: 180 },
                          py: { xs: 1.1, sm: 0.75 },
                        }}
                      >
                        {isExporting ? 'Downloading…' : 'Download Excel'}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>

                {downloadProgress !== null && (
                  <Box sx={{ mt: { xs: 2, sm: 2.5 } }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 1,
                        mb: 0.75,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          minWidth: 0,
                          flex: 1,
                          wordBreak: 'break-word',
                        }}
                      >
                        {downloadLabel || 'Downloading…'}
                      </Typography>
                      <Typography variant="body2" fontWeight={600} color="primary.main">
                        {Math.round(downloadProgress)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={downloadProgress}
                      sx={{
                        height: { xs: 6, sm: 8 },
                        borderRadius: 4,
                        backgroundColor: alpha(theme.palette.primary.main, 0.12),
                      }}
                    />
                  </Box>
                )}

                {error && (
                  <Alert
                    severity="error"
                    sx={{ mt: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                    onClose={() => setError('')}
                  >
                    {error}
                  </Alert>
                )}
                {success && (
                  <Alert
                    severity="success"
                    sx={{ mt: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                    onClose={() => setSuccess('')}
                  >
                    {success}
                  </Alert>
                )}
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 2, md: 3 },
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor: theme.palette.background.paper,
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 2,
                    flexWrap: 'wrap',
                  }}
                >
                  <Schedule color="action" />
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    sx={{
                      flexGrow: 1,
                      fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' },
                      minWidth: 0,
                    }}
                  >
                    Recent downloads
                  </Typography>
                  {history.length > 0 && (
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      startIcon={<DeleteOutline />}
                      onClick={clearHistory}
                      sx={{
                        flexShrink: 0,
                        fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                      }}
                    >
                      {isMobile ? 'Clear' : 'Clear all'}
                    </Button>
                  )}
                </Box>

                {history.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No reports downloaded yet. Generate one above to see it listed here.
                  </Typography>
                ) : isMobile ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    {history.map((row) => (
                      <Box
                        key={row.id}
                        sx={{
                          p: 1.5,
                          borderRadius: 1.5,
                          border: `1px solid ${theme.palette.divider}`,
                          backgroundColor: alpha(theme.palette.primary.main, 0.02),
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {row.siteName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {row.siteId}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                            <Chip size="small" color="success" label={row.status || 'Downloaded'} />
                            <IconButton
                              size="small"
                              color="error"
                              aria-label="Delete download record"
                              onClick={() => removeHistoryEntry(row.id)}
                            >
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Tag: <Box component="span" sx={{ color: 'text.primary', fontWeight: 500 }}>{row.tagName || '—'}</Box>
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Period: {row.period}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ wordBreak: 'break-all' }}>
                          {row.startTime} → {row.endTime}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          {row.generatedAt
                            ? new Date(row.generatedAt).toLocaleString()
                            : '—'}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <TableContainer sx={{ overflowX: 'auto', maxWidth: '100%' }}>
                    <Table size="medium">
                      <TableHead>
                        <TableRow>
                          <TableCell>Site</TableCell>
                          <TableCell>Tag</TableCell>
                          <TableCell>Period</TableCell>
                          <TableCell>Range (UTC)</TableCell>
                          <TableCell>Generated</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {history.map((row) => (
                          <TableRow key={row.id}>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>
                                {row.siteName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {row.siteId}
                              </Typography>
                            </TableCell>
                            <TableCell>{row.tagName || '—'}</TableCell>
                            <TableCell>{row.period}</TableCell>
                            <TableCell>
                              <Typography variant="caption" display="block">
                                {row.startTime}
                              </Typography>
                              <Typography variant="caption" display="block">
                                → {row.endTime}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {row.generatedAt
                                ? new Date(row.generatedAt).toLocaleString()
                                : '—'}
                            </TableCell>
                            <TableCell>
                              <Chip size="small" color="success" label={row.status || 'Downloaded'} />
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Delete record">
                                <IconButton
                                  size="small"
                                  color="error"
                                  aria-label="Delete download record"
                                  onClick={() => removeHistoryEntry(row.id)}
                                >
                                  <DeleteOutline fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default ReportsAnalyticsPage;
